import React from 'react';
import {
  Box,
  VStack,
  Text,
  Badge,
  HStack,
  Switch,
  FormControl,
  FormLabel,
  useColorModeValue,
  Tooltip,
  Icon,
  IconButton
} from '@chakra-ui/react';
import { MapPin, Ruler, Eye, EyeSlash, Trash } from '@phosphor-icons/react';
import { useMedia } from '../../hooks/useMedia';

const DetectionsList = ({
  detections,
  showBoundingBoxes,
  setShowBoundingBoxes,
  visibleFeatures = {},
  onFeatureToggle,
  onDeleteFeature,
  mediaId,
  siteId
}) => {
  // Replaced useColorModeValue with direct semantic tokens below
  // const boxBg = useColorModeValue('gray.50', 'gray.700'); // Replaced -> bg.accent
  // const detectionBoxBg = useColorModeValue('white', 'gray.600'); // Replaced -> bg.surface
  // const textColor = useColorModeValue('gray.800', 'white'); // Replaced -> text.default
  // const accentColor = useColorModeValue('blue.500', 'blue.300'); // Replaced -> interactive.primary
  const { updateFeatures } = useMedia(siteId);

  const handleDelete = async (index) => {
    // Create new array without the deleted detection
    const updatedDetections = detections.filter((_, i) => i !== index);
    
    if (mediaId && siteId) {
      // If we have mediaId and siteId, this is after upload - update Firebase
      try {
        const result = await updateFeatures(mediaId, updatedDetections);
        if (result.success) {
          if (onDeleteFeature) {
            onDeleteFeature(index);
          }
        }
      } catch (error) {
        console.error('Error updating features:', error);
      }
    } else {
      // Before upload - just update local state
      if (onDeleteFeature) {
        onDeleteFeature(index);
      }
    }
  };

  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="md" p={4} bg="bg.accent" maxH="calc(100vh - 250px)" overflowY="auto"> {/* Use theme tokens */}
      <VStack spacing={4} align="stretch">
        <FormControl display="flex" alignItems="center" justifyContent="space-between">
          <FormLabel htmlFor="show-boxes" mb="0" color="text.default"> {/* Use theme token */}
            Show All Bounding Boxes
          </FormLabel>
          <Switch
            id="show-boxes"
            isChecked={showBoundingBoxes}
            onChange={(e) => setShowBoundingBoxes(e.target.checked)}
            colorScheme="success" // Use theme token
          />
        </FormControl>

        <Text fontWeight="bold" color="text.default">Detected Features:</Text> {/* Use theme token */}
        {detections.map((d, i) => {
          const isVisible = visibleFeatures[d.class] !== false;
          
          return (
            <Box 
              key={i}
              p={4}
              borderWidth="1px"
              borderColor="border.default" // Add theme border
              borderRadius="lg"
              bg="bg.surface" // Use theme token
              color="text.default" // Use theme token
              shadow="sm"
            >
              <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge
                    colorScheme="brand.primary" // Use theme scheme
                    fontSize={{ base: 'xs', md: 'sm' }} // Responsive font
                    borderRadius="full" 
                    px={2}
                  >
                    #{i + 1}
                  </Badge>
                  <Badge
                    as="button"
                    colorScheme="brand.primary" // Use theme scheme
                    fontSize={{ base: 'xs', md: 'sm' }} // Responsive font
                    px={2}
                    py={1}
                    display="flex"
                    alignItems="center"
                    cursor="pointer"
                    // Removed hardcoded transition
                    _hover={{
                      transform: 'scale(1.05)',
                      boxShadow: 'sm'
                    }}
                    onClick={() => {
                      // Trigger filtering functionality
                      const url = new URL(window.location);
                      url.searchParams.set('feature', d.class.toLowerCase());
                      window.history.pushState({}, '', url);
                      window.dispatchEvent(new CustomEvent('updateFilters', { 
                        detail: { feature: d.class.toLowerCase() } 
                      }));
                    }}
                  >
                    {d.class}
                  </Badge>
                </HStack>
                <HStack spacing={2}>
                  <IconButton
                    icon={isVisible ? <Eye /> : <EyeSlash />}
                    size="sm"
                    colorScheme={isVisible ? "success" : "gray"} // Use theme scheme
                    onClick={() => onFeatureToggle(d.class)}
                    aria-label={isVisible ? "Hide feature" : "Show feature"}
                  />
                  <IconButton
                    icon={<Trash />}
                    size="sm"
                    colorScheme="error" // Use theme scheme
                    onClick={() => handleDelete(i)}
                    aria-label="Delete detection"
                  />
                </HStack>
              </HStack>
              
              <VStack spacing={2} align="stretch">
                <Tooltip label="Feature position on image" placement="top">
                  <HStack spacing={2}>
                    <Icon as={MapPin} color="interactive.primary" /> {/* Use theme token */}
                    <Text fontSize={{ base: 'xs', md: 'sm' }}> {/* Responsive font */}
                      Location: ({Math.round(d.x)}, {Math.round(d.y)})
                    </Text>
                  </HStack>
                </Tooltip>
                
                <Tooltip label="Feature size in pixels" placement="top">
                  <HStack spacing={2}>
                    <Icon as={Ruler} color="interactive.primary" /> {/* Use theme token */}
                    <Text fontSize={{ base: 'xs', md: 'sm' }}> {/* Responsive font */}
                      Size: {Math.round(d.width)} × {Math.round(d.height)} px
                    </Text>
                  </HStack>
                </Tooltip>
              </VStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

export default DetectionsList;
